import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { promotionService } from '../services/promotionService';
import AdminHeader from '../components/AdminHeader';
import ConfirmModal from '../components/ConfirmModal';
import { escapeCSV, downloadCSV, formatDate } from '../utils/csvExport';
import '../styles/AdminPromotions.css';
import './AdminPromotions_Mobile.css';

function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ 
    code: '', 
    type: '%', 
    amount: '', 
    validFrom: '', 
    validTo: '', 
    active: true,
    limitedUses: false, // if true, `uses` is respected; otherwise unlimited
    uses: '', // number of allowed uses when limitedUses is true
    requireMinCart: false, // if true, `minCartValue` is respected
    minCartValue: '', // minimum cart value required for promo
    resetRedeemed: false // when true, admin requests to reset per-user redemption counts
  });
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePromoId, setDeletePromoId] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Load promotions from database
  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const data = await promotionService.getAllPromotions();
      setPromos(data);
      setError(null);
    } catch (err) {
      setError('Failed to load promotions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (promo = null) => {
    if (promo) {
      setForm({
        code: promo.code,
        // UI uses `type`/`amount` but backend uses `discountType`/`value`
        type: promo.discountType || promo.type || '%',
        amount: promo.value ?? promo.amount ?? '',
        validFrom: promo.validFrom.split('T')[0],
        validTo: promo.validTo.split('T')[0],
        active: promo.active,
  limitedUses: !!promo.limitedUses,
  uses: promo.perUserLimit ?? '',
        requireMinCart: promo.minCartAmount !== undefined && promo.minCartAmount !== null,
        minCartValue: promo.minCartAmount ?? ''
      , resetRedeemed: false
      });
      setEditId(promo._id);
    } else {
      setForm({ 
        code: '', 
        type: '%', 
        amount: '', 
        validFrom: '', 
        validTo: '', 
        active: true,
  limitedUses: false,
  uses: '',
        requireMinCart: false,
        minCartValue: '',
        resetRedeemed: false
      });
      setEditId(null);
    }
    setShowModal(true);
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => setForm(f => ({ ...f, [name]: checked }));

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // Sanitize/convert fields before sending to API
      // Map frontend form fields to backend promotion schema
      const payload = {
        code: form.code,
        // backend field name
        discountType: form.type,
        // backend expects `value`
        value: parseFloat(form.amount) || 0,
        validFrom: form.validFrom,
        validTo: form.validTo,
        active: !!form.active,
        // backend has minCartAmount
        minCartAmount: form.requireMinCart ? (parseFloat(form.minCartValue) || 0) : 0,
  // map frontend limitedUses/uses to backend perUserLimit (per-user redemption limit)
  limitedUses: !!form.limitedUses,
  perUserLimit: form.limitedUses ? (parseInt(form.uses, 10) || 0) : null,
  // admin request to reset per-user redeemed counts
  resetRedeemed: !!form.resetRedeemed
      };

      if (editId) {
        await promotionService.updatePromotion(editId, payload);
      } else {
        await promotionService.createPromotion(payload);
      }
      await loadPromotions();
      setShowModal(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save promotion');
    }
  };

  const handleDelete = async (id) => {
    setDeletePromoId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletePromoId) return;
    try {
      await promotionService.deletePromotion(deletePromoId);
      await loadPromotions();
      setError(null);
    } catch (err) {
      setError('Failed to delete promotion');
    }
    setDeletePromoId(null);
  };

  const handleActivate = async (id) => {
    try {
      await promotionService.activatePromotion(id);
      await loadPromotions();
      setError(null);
    } catch (err) {
      setError('Failed to activate promotion');
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await promotionService.deactivatePromotion(id);
      await loadPromotions();
      setError(null);
    } catch (err) {
      setError('Failed to deactivate promotion');
    }
  };

  // Bulk actions
  const handleSelect = (id) => {
    setSelected(selected.includes(id)
      ? selected.filter(i => i !== id)
      : [...selected, id]);
  };

  const handleBulkActivate = async () => {
    if (selected.length === 0) return;
    try {
      await promotionService.bulkActivate(selected);
      await loadPromotions();
      setSelected([]);
      setError(null);
    } catch (err) {
      setError('Failed to activate promotions');
    }
  };

  const handleBulkDeactivate = async () => {
    if (selected.length === 0) return;
    try {
      await promotionService.bulkDeactivate(selected);
      await loadPromotions();
      setSelected([]);
      setError(null);
    } catch (err) {
      setError('Failed to deactivate promotions');
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await promotionService.bulkDelete(selected);
      await loadPromotions();
      setSelected([]);
      setError(null);
    } catch (err) {
      setError('Failed to delete promotions');
    }
  };

  const exportToCSV = () => {
    let csvContent = 'Code,Type,Amount,Valid From,Valid To,Min Cart Value,Per User Limit,Active,Times Used\n';
    
    promos.forEach(promo => {
      const code = escapeCSV(promo.code || '');
      const type = escapeCSV(promo.discountType || promo.type || '');
      const amount = escapeCSV(promo.value ?? promo.amount ?? '');
      const validFrom = escapeCSV(promo.validFrom ? formatDate(promo.validFrom) : '');
      const validTo = escapeCSV(promo.validTo ? formatDate(promo.validTo) : '');
      const minCart = escapeCSV(promo.minCartAmount ?? 'N/A');
      const perUserLimit = escapeCSV(promo.perUserLimit ?? 'Unlimited');
      const active = escapeCSV(promo.active ? 'Yes' : 'No');
      const timesUsed = escapeCSV(promo.timesUsed || 0);
      
      csvContent += `${code},${type},${amount},${validFrom},${validTo},${minCart},${perUserLimit},${active},${timesUsed}\n`;
    });
    
    const filename = `promo_codes_backup_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  if (loading) {
    return (
      <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Green Header Bar */}
        <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
        
        {/* Navbar */}
        <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
          <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
            <AdminHeader />
          </div>
        </div>

        {/* Loading State */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
            style={{ fontSize: '4rem' }}
          >
            🌱
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
        {/* Empty green bar for accent */}
      </div>
      {/* Navbar below header, white background */}
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>
      <div style={{ width: '100%', margin: '40px auto', padding: '0 20px', flex: 1, boxSizing: 'border-box' }}>
        <div className="admin-promotions admin-promos-page">
          <motion.div
            className="promos-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1>Manage Promo Codes</h1>
            <div style={{ display: 'flex', gap: 12 }}>
              <motion.button 
                onClick={exportToCSV}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: '#2e7d32',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(46,125,50,0.2)'
                }}
              >
                📊 Export CSV
              </motion.button>
              <motion.button 
                className="add-promo-btn"
                onClick={() => openModal()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                + Add Promotion
              </motion.button>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div 
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selected.length > 0 && (
              <motion.div 
                className="bulk-actions"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span className="bulk-count">{selected.length} selected</span>
                <div className="bulk-buttons">
                  <motion.button 
                    onClick={handleBulkActivate}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-activate"
                  >
                    ✓ Activate
                  </motion.button>
                  <motion.button 
                    onClick={handleBulkDeactivate}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-deactivate"
                  >
                    ✕ Deactivate
                  </motion.button>
                  <motion.button 
                    onClick={handleBulkDelete}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-delete"
                  >
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg> Delete
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

      {promos.length === 0 ? (
            <motion.div 
              className="empty-state"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="empty-state-icon">🎫</div>
              <h3 className="empty-state-title">No Promotions Yet</h3>
              <p className="empty-state-text">Create your first promotion to start offering discounts to customers</p>
            </motion.div>
          ) : (
            <>
              {/* Desktop Table View */}
              <motion.div 
                className="promos-table-container desktop-only"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <table className="promos-table">
                  <thead>
                    <tr>
                      <th><input 
                        type="checkbox" 
                        onChange={(e) => setSelected(e.target.checked ? promos.map(p => p._id) : [])}
                        checked={selected.length === promos.length && promos.length > 0}
                      /></th>
                      <th>Code</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Per-user limit</th>
                      <th>Min Cart</th>
                      <th>Valid From</th>
                      <th>Valid To</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence mode="popLayout">
                      {promos.map((promo, index) => (
                        <motion.tr 
                          key={promo._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.2, delay: index * 0.03 }}
                          layout
                        >
                          <td>
                            <input 
                              type="checkbox" 
                              checked={selected.includes(promo._id)}
                              onChange={() => handleSelect(promo._id)}
                            />
                          </td>
                          <td><span className="promo-code">{promo.code}</span></td>
                          <td>
                            <span className={`type-badge ${(promo.discountType || promo.type) === '%' ? 'percent' : 'fixed'}`}>
                              {(promo.discountType || promo.type) === '%' ? '📊 Percentage' : '💰 Fixed'}
                            </span>
                          </td>
                          <td><span className="discount-value">{(promo.discountType || promo.type) === '%' ? `${promo.value}%` : `₱${promo.value}`}</span></td>
                          <td>{promo.perUserLimit ? promo.perUserLimit : 'Unlimited'}</td>
                          <td>{promo.minCartAmount !== undefined && promo.minCartAmount !== null ? `₱${promo.minCartAmount}` : '-'}</td>
                          <td>{new Date(promo.validFrom).toLocaleDateString()}</td>
                          <td>{new Date(promo.validTo).toLocaleDateString()}</td>
                          <td>
                            <span className={`status-badge ${promo.active ? 'active' : 'inactive'}`}>
                              {promo.active ? '✓ Active' : '✕ Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <motion.button 
                                onClick={() => openModal(promo)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn-edit"
                                title="Edit"
                              >
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                              </motion.button>
                              {promo.active ? (
                                <motion.button 
                                  onClick={() => handleDeactivate(promo._id)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="btn-toggle active"
                                  title="Deactivate"
                                >
                                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                                </motion.button>
                              ) : (
                                <motion.button 
                                  onClick={() => handleActivate(promo._id)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="btn-toggle"
                                  title="Activate"
                                >
                                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                </motion.button>
                              )}
                              <motion.button 
                                onClick={() => handleDelete(promo._id)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn-delete"
                                title="Delete"
                              >
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </motion.div>

              {/* Mobile Card View */}
              <motion.div 
                className="promos-grid mobile-only"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <AnimatePresence mode="popLayout">
                  {promos.map((promo, index) => (
                    <motion.div
                      key={promo._id}
                      className="promo-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      layout
                    >
                      <div className="card-header">
                        <div className="card-select">
                          <input 
                            type="checkbox"
                            className="checkbox-large"
                            checked={selected.includes(promo._id)}
                            onChange={() => handleSelect(promo._id)}
                          />
                        </div>
                        <div className="card-title-section">
                          <h3 className="promo-code">{promo.code}</h3>
                          <span className={`status-badge ${promo.active ? 'active' : 'inactive'}`}>
                            {promo.active ? '✓ Active' : '✕ Inactive'}
                          </span>
                        </div>
                      </div>

                      <div className="card-discount">
                        <span className="discount-label">Discount</span>
                        <span className="discount-value">
                          {(promo.discountType || promo.type) === '%' ? `${promo.value}%` : `₱${promo.value}`}
                        </span>
                      </div>

                      <div className="card-details">
                        <div className="detail-item">
                          <span className="detail-label">Type</span>
                          <span className="detail-value">
                            <span className={`type-badge ${(promo.discountType || promo.type) === '%' ? 'percent' : 'fixed'}`}>
                              {(promo.discountType || promo.type) === '%' ? '📊 Percentage' : '💰 Fixed'}
                            </span>
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Per-user limit</span>
                          <span className="detail-value">{promo.perUserLimit ? promo.perUserLimit : 'Unlimited'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Min Cart</span>
                          <span className="detail-value">{promo.minCartAmount !== undefined && promo.minCartAmount !== null ? `₱${promo.minCartAmount}` : '-'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Valid Period</span>
                          <span className="detail-value">{new Date(promo.validFrom).toLocaleDateString()} - {new Date(promo.validTo).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className="card-actions">
                        <motion.button
                          onClick={() => openModal(promo)}
                          className="btn-card-edit"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                          Edit Promotion
                        </motion.button>
                        {promo.active ? (
                          <motion.button
                            onClick={() => handleDeactivate(promo._id)}
                            className="btn-card-toggle active"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                            Deactivate
                          </motion.button>
                        ) : (
                          <motion.button
                            onClick={() => handleActivate(promo._id)}
                            className="btn-card-toggle"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            Activate
                          </motion.button>
                        )}
                        <motion.button
                          onClick={() => handleDelete(promo._id)}
                          className="btn-card-delete"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                          Delete
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </>
          )}

      <AnimatePresence>
        {showModal && (
          <motion.div 
            className="modal-overlay" 
            onClick={() => setShowModal(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div 
              className="promo-modal" 
              onClick={e => e.stopPropagation()}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="modal-header">
                <h2>
                  🎫 {editId ? 'Edit Promotion' : 'Add Promotion'}
                </h2>
                <button 
                  type="button" 
                  className="modal-close"
                  onClick={() => setShowModal(false)}
                >
                  ×
                </button>
              </div>

              <form className="promo-form" onSubmit={handleSave}>
                <div className="form-group">
                  <label className="form-label required">Promotion Code</label>
                  <input 
                    className="form-input"
                    name="code" 
                    value={form.code} 
                    onChange={handleFormChange} 
                    placeholder="e.g., SUMMER2024"
                    required 
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Discount Type</label>
                    <select 
                      className="form-select"
                      name="type" 
                      value={form.type} 
                      onChange={handleFormChange}
                    >
                      <option value="%">📊 Percentage</option>
                      <option value="fixed">💰 Fixed Amount</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label required">Amount</label>
                    <input 
                      className="form-input"
                      type="number" 
                      name="amount" 
                      value={form.amount} 
                      onChange={handleFormChange} 
                      min="0"
                      step="0.01"
                      placeholder={form.type === '%' ? 'e.g., 20' : 'e.g., 100'}
                      required 
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Valid From</label>
                    <input 
                      className="form-input"
                      type="date" 
                      name="validFrom" 
                      value={form.validFrom} 
                      onChange={handleFormChange} 
                      required 
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label required">Valid To</label>
                    <input 
                      className="form-input"
                      type="date" 
                      name="validTo" 
                      value={form.validTo} 
                      onChange={handleFormChange} 
                      min={form.validFrom || undefined}
                      required 
                    />
                    {form.validFrom && form.validTo && new Date(form.validTo) < new Date(form.validFrom) && (
                      <div style={{ color: 'red', fontSize: '0.85rem', marginTop: '4px' }}>
                        Valid To date must be after Valid From date
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox" 
                        name="active" 
                        checked={form.active}
                        onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      />
                      <span>✓ Active (visible to customers)</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="limitedUses"
                        checked={!!form.limitedUses}
                        onChange={(e) => setForm(f => ({ ...f, limitedUses: e.target.checked }))}
                      />
                      <span>🔢 Limit uses per customer</span>
                    </label>

                    <AnimatePresence>
                      {form.limitedUses && (
                        <motion.div 
                          className="form-group nested"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <label className="form-label required">Max uses per customer</label>
                          <input
                            className="form-input"
                            type="number"
                            name="uses"
                            value={form.uses}
                            onChange={handleFormChange}
                            min="1"
                            step="1"
                            placeholder="e.g., 3"
                            required
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div className="form-group">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="requireMinCart"
                        checked={!!form.requireMinCart}
                        onChange={(e) => handleCheckboxChange('requireMinCart', e.target.checked)}
                      />
                      <span>🛒 Require minimum cart value</span>
                    </label>

                    <AnimatePresence>
                      {form.requireMinCart && (
                        <motion.div 
                          className="form-group nested"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <label className="form-label required">Minimum cart value (₱)</label>
                          <input
                            className="form-input"
                            type="number"
                            name="minCartValue"
                            value={form.minCartValue}
                            onChange={handleFormChange}
                            min="0"
                            step="0.01"
                            placeholder="e.g., 500"
                            required
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {editId && (
                  <div className="form-group">
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="resetRedeemed"
                          checked={!!form.resetRedeemed}
                          onChange={(e) => handleCheckboxChange('resetRedeemed', e.target.checked)}
                        />
                        <span>🔄 Reset all customer redemption counts</span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="modal-actions">
                  <motion.button 
                    type="submit" 
                    className="btn-save"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    Save Promotion
                  </motion.button>
                  <motion.button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setShowModal(false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
        </div>
      </div>
      {/* Green Footer Bar */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, letterSpacing: 1 }}>© {new Date().getFullYear()} Chiqui's Garden</span>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletePromoId(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Promo Code"
        message="Are you sure you want to delete this promo code? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Multiple Promo Codes"
        message={`Are you sure you want to delete ${selected.length} promo code(s)? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
        type="error"
      />
    </div>
  );
}

export default AdminPromotions;
